import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Redirect, RouteComponentProps, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IBankHoliday } from '@cbgms/api'
import { useNotifier } from '@cbgms/base/notifier'
import { useQuery } from '@cbgms/base/http/use-query'
import HolidayForm from './HolidayForm'
import { deleteBankHoliday, updateBankHoliday } from '../state/actions'
import { preloadDataForEditHoliday } from '../utils/data-helpers'
import { Loader } from '@carsys/ui'
import { IDeleteOptions } from 'core/page/layout/FormikFormLayout'

type Props = RouteComponentProps<{ bankHolidayUUID: string }>

const EditHoliday = (props: Props) => {
  const {
    match: {
      params: { bankHolidayUUID }
    }
  } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const history = useHistory()
  const preloadData = useQuery(['EditHoliday', 'preloadDataForEditHoliday', bankHolidayUUID], async () => {
    return preloadDataForEditHoliday(dispatch, bankHolidayUUID)
  })

  const onSubmit = async (values: IBankHoliday) => {
    try {
      await dispatch(updateBankHoliday(bankHolidayUUID, values))
      return values
    } catch (error) {
      throw error
    }
  }

  const onSubmitSuccess = async (_: IBankHoliday) => {
    notifier.success(t('bank_holidays.successfully_edited'))
  }
  const deleteOptions = useMemo(
    (): IDeleteOptions => ({
      onDelete: async () => {
        await dispatch(deleteBankHoliday(bankHolidayUUID))
        return { Data: { Rejections: [], Warnings: [] } }
      },
      onDeleteSuccess: async () => history.push('/bank-holidays'),
      itemName: preloadData?.data?.initialBankHolidayData?.Name || '',
      deleteScope: 'bank_holiday'
    }),
    [bankHolidayUUID, preloadData?.data?.initialBankHolidayData?.Name]
  )

  if (preloadData.isLoading || !preloadData.data) {
    return <Loader loading />
  }
  if (preloadData.isError) {
    notifier.error(t('global.something_went_wrong'))
    return <Redirect to='/bank-holidays' />
  }

  return (
    <HolidayForm
      regionTags={preloadData.data.regionTags}
      headerText='bank_holidays.edit_holiday'
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      form='edit_bank_holiday'
      initialValues={preloadData.data.initialBankHolidayData}
      deleteOptions={deleteOptions}
      enableReinitialize
    />
  )
}

export default EditHoliday
